import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import {
  Alert,
  Anchor,
  Button,
  FileButton,
  Group,
  Loader,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconPlayerPlay, IconUpload } from '@tabler/icons-react';
import { WidgetNodeShell } from '../../components/flow-editor/nodes/widget-node-shell';
import { LabeledHandle } from '../../components/flow-editor/nodes/labeled-handle';
import { useOrg } from '../../hooks/use-org';
import { getErrorMessage } from '../../lib/error-message';
import { useAudioFiles, uploadAudioFile } from '../audio-files';
import type { WidgetDefinition, WidgetNodeProps, ConfigPanelProps } from '../types';
import {
  getPlayAudioSource,
  getPlayCustomUrl,
  getPlayPreviewLabel,
  hasPlayAudioSelection,
  playWidgetCore,
  type PlayConfig,
} from '@legacy-shared/widgets/core/play';

export { type PlayConfig } from '@legacy-shared/widgets/core/play';

function PlayNode({ id, data, selected }: WidgetNodeProps<PlayConfig>) {
  const preview = getPlayPreviewLabel(data.config);

  return (
    <WidgetNodeShell
      icon={IconPlayerPlay}
      title={data.label || 'Play'}
      subtitle={preview}
      configured={hasPlayAudioSelection(data.config)}
      errors={data.errors}
      selected={selected}
      color="green"
    >
      <Handle type="target" position={Position.Top} id="in" />
      <LabeledHandle type="source" position="bottom" id="out" label="Next" offset="50%" nodeId={id} />
    </WidgetNodeShell>
  );
}

function PlayConfigPanel({ config, onChange }: ConfigPanelProps<PlayConfig>) {
  const { organizationId, currentOrg } = useOrg();
  const audioFiles = useAudioFiles(organizationId);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const source = getPlayAudioSource(config);
  const isOrgAdmin = currentOrg?.role === 'owner' || currentOrg?.role === 'admin';
  const audioFileOptions = (audioFiles ?? []).map((audioFile) => ({
    value: audioFile.id,
    label: audioFile.name,
  }));

  async function handleUpload(file: File | null) {
    if (!file || !organizationId || !isOrgAdmin) return;

    setUploadError(null);
    setIsUploading(true);

    try {
      const audioFile = await uploadAudioFile(organizationId, file);
      onChange({
        ...config,
        source: 'catalog',
        audioFileId: audioFile.id,
        audioFileName: audioFile.name,
        audioUrl: audioFile.audioUrl,
      });
    } catch (error) {
      setUploadError(getErrorMessage(error, 'Upload failed'));
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <Stack gap="md">
      <SegmentedControl
        value={source}
        onChange={(value) =>
          onChange({
            ...config,
            source: value as PlayConfig['source'],
            customUrl: value === 'custom_url' ? getPlayCustomUrl(config) : config.customUrl,
          })
        }
        data={[
          { label: 'Media Catalog', value: 'catalog' },
          { label: 'Custom URL', value: 'custom_url' },
        ]}
      />

      {source === 'catalog' ? (
        <Stack gap="xs">
          <Select
            label="Audio Media"
            description="Choose from audio uploaded to this organization"
            placeholder={audioFiles?.length ? 'Select audio media' : 'Upload audio to get started'}
            data={audioFileOptions}
            value={config.audioFileId || null}
            onChange={(value) => {
              const audioFile = audioFiles?.find((item) => item.id === value);
              onChange({
                ...config,
                source: 'catalog',
                audioFileId: value ?? '',
                audioFileName: audioFile?.name ?? '',
                audioUrl: audioFile?.audioUrl ?? '',
              });
            }}
            searchable
            clearable
            disabled={!organizationId}
            rightSection={audioFiles === undefined ? <Loader size="xs" /> : undefined}
          />
          <Group justify="space-between" align="center">
            <Text size="sm" c="dimmed">
              {audioFiles?.length
                ? `${audioFiles.length} audio item${audioFiles.length === 1 ? '' : 's'} available`
                : 'No audio uploaded yet'}
            </Text>
            {isOrgAdmin ? (
              <FileButton onChange={handleUpload} accept="audio/*,.aac,.flac,.m4a,.mp3,.ogg,.wav,.webm">
                {(fileButtonProps) => (
                  <Button
                    {...fileButtonProps}
                    variant="light"
                    leftSection={<IconUpload size={16} />}
                    loading={isUploading}
                    disabled={!organizationId}
                  >
                    Upload Audio
                  </Button>
                )}
              </FileButton>
            ) : null}
          </Group>
          {isOrgAdmin ? (
            <Anchor component={Link} to="/settings/media" size="sm">
              Open media management
            </Anchor>
          ) : null}
          {uploadError ? (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {uploadError}
            </Alert>
          ) : null}
        </Stack>
      ) : (
        <TextInput
          label="Custom Audio URL"
          description="Use an external audio file URL instead of the media catalog"
          placeholder="https://example.com/audio.mp3"
          value={getPlayCustomUrl(config)}
          onChange={(event) =>
            onChange({
              ...config,
              source: 'custom_url',
              customUrl: event.currentTarget.value,
            })
          }
        />
      )}

      <NumberInput
        label="Loop"
        description="Number of times to repeat the audio"
        value={config.loop}
        onChange={(value) => onChange({ ...config, loop: typeof value === 'number' ? value : 1 })}
        min={1}
        max={100}
      />
    </Stack>
  );
}

export const playWidget: WidgetDefinition<PlayConfig> = {
  ...playWidgetCore,
  icon: 'IconPlayerPlay',

  handles: {
    inputs: [{ id: 'in', type: 'default', label: 'Input', position: 'top' }],
    outputs: [{ id: 'out', type: 'default', label: 'Next', position: 'bottom' }],
  },

  connectionRules: {
    allowedInputFrom: ['*'],
    allowedOutputTo: ['*'],
  },

  ui: {
    component: PlayNode,
    configPanel: PlayConfigPanel,
    preview: getPlayPreviewLabel,
  },

  validation: {
    ...playWidgetCore.validation,
    validateConnections: () => ({ valid: true, errors: [] }),
  },
};
